<script setup>
import { ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useAccount } from 'dashboard/composables/useAccount';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SettingsLayout from '../SettingsLayout.vue';
import WithLabel from 'v3/components/Form/WithLabel.vue';
import TextInput from 'next/input/Input.vue';
import Switch from 'next/switch/Switch.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

const DEFAULT_MIN_DETECT_CHARS = 20;
const DEFAULT_DETECT_CONFIDENCE = 0.75;
const DEFAULT_MODEL = 'gpt-4o-mini';

const { t } = useI18n();
const { currentAccount, updateAccount } = useAccount();

const enabled = ref(true);
const minDetectChars = ref(DEFAULT_MIN_DETECT_CHARS);
const detectConfidence = ref(DEFAULT_DETECT_CONFIDENCE);
const model = ref(DEFAULT_MODEL);
const isSubmitting = ref(false);

const toNumber = value => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

watch(
  currentAccount,
  () => {
    const settings = currentAccount.value?.settings || {};

    enabled.value = settings.cw_translation_layer_enabled !== false;

    const configuredMinChars = toNumber(
      settings.cw_translation_min_detect_chars
    );
    minDetectChars.value =
      configuredMinChars && configuredMinChars > 0
        ? Math.round(configuredMinChars)
        : DEFAULT_MIN_DETECT_CHARS;

    const configuredConfidence = toNumber(
      settings.cw_translation_detect_confidence
    );
    detectConfidence.value =
      configuredConfidence !== null &&
      configuredConfidence >= 0 &&
      configuredConfidence <= 1
        ? configuredConfidence
        : DEFAULT_DETECT_CONFIDENCE;

    model.value = settings.cw_translation_model || DEFAULT_MODEL;
  },
  { immediate: true, deep: true }
);

const validate = () => {
  const minChars = toNumber(minDetectChars.value);
  if (minChars === null || minChars < 1) {
    useAlert(t('LIVE_TRANSLATIONS.FORM.MIN_DETECT_CHARS.ERROR'));
    return false;
  }

  const confidence = toNumber(detectConfidence.value);
  if (confidence === null || confidence < 0 || confidence > 1) {
    useAlert(t('LIVE_TRANSLATIONS.FORM.DETECT_CONFIDENCE.ERROR'));
    return false;
  }

  return true;
};

const saveSettings = async () => {
  if (!validate()) {
    return;
  }

  try {
    isSubmitting.value = true;
    await updateAccount(
      {
        cw_translation_layer_enabled: enabled.value,
        cw_translation_min_detect_chars: Math.round(
          Number(minDetectChars.value)
        ),
        cw_translation_detect_confidence: Number(detectConfidence.value),
        cw_translation_model: (model.value || '').trim() || DEFAULT_MODEL,
      },
      { silent: true }
    );
    useAlert(t('LIVE_TRANSLATIONS.UPDATE.SUCCESS'));
  } catch (_error) {
    useAlert(t('LIVE_TRANSLATIONS.UPDATE.ERROR'));
  } finally {
    isSubmitting.value = false;
  }
};
</script>

<template>
  <SettingsLayout :no-records-found="false">
    <template #header>
      <BaseSettingsHeader
        :title="$t('LIVE_TRANSLATIONS.HEADER.TITLE')"
        :description="$t('LIVE_TRANSLATIONS.HEADER.DESCRIPTION')"
      />
    </template>

    <template #body>
      <div
        class="flex flex-col w-full outline-1 outline outline-n-container rounded-xl bg-n-solid-2 divide-y divide-n-weak"
      >
        <div class="flex flex-col gap-2 items-start px-5 py-4">
          <div class="flex justify-between items-center w-full">
            <h3 class="text-base font-medium text-n-slate-12">
              {{ t('LIVE_TRANSLATIONS.FORM.TITLE') }}
            </h3>
            <div class="flex justify-end">
              <Switch v-model="enabled" />
            </div>
          </div>
          <p class="mb-0 text-sm text-n-slate-11">
            {{ t('LIVE_TRANSLATIONS.FORM.NOTE') }}
          </p>
        </div>

        <div class="px-5 py-4">
          <form class="grid gap-5" @submit.prevent="saveSettings">
            <WithLabel
              :label="t('LIVE_TRANSLATIONS.FORM.MIN_DETECT_CHARS.LABEL')"
              :help-message="t('LIVE_TRANSLATIONS.FORM.MIN_DETECT_CHARS.HELP')"
            >
              <TextInput
                v-model="minDetectChars"
                type="number"
                min="1"
                step="1"
                class="w-full"
              />
            </WithLabel>

            <WithLabel
              :label="t('LIVE_TRANSLATIONS.FORM.DETECT_CONFIDENCE.LABEL')"
              :help-message="t('LIVE_TRANSLATIONS.FORM.DETECT_CONFIDENCE.HELP')"
            >
              <TextInput
                v-model="detectConfidence"
                type="number"
                min="0"
                max="1"
                step="0.01"
                class="w-full"
              />
            </WithLabel>

            <WithLabel
              :label="t('LIVE_TRANSLATIONS.FORM.MODEL.LABEL')"
              :help-message="t('LIVE_TRANSLATIONS.FORM.MODEL.HELP')"
            >
              <TextInput
                v-model="model"
                type="text"
                class="w-full"
                :placeholder="t('LIVE_TRANSLATIONS.FORM.MODEL.PLACEHOLDER')"
              />
            </WithLabel>

            <div class="flex gap-2">
              <NextButton
                blue
                type="submit"
                :is-loading="isSubmitting"
                :label="t('LIVE_TRANSLATIONS.FORM.SAVE')"
              />
            </div>
          </form>
        </div>
      </div>
    </template>
  </SettingsLayout>
</template>
